import React, { useState } from 'react';
import { toast } from 'react-toastify';

import Box from '@material-ui/core/Box';
import Grid from '@material-ui/core/Grid';
import IconButton from '@material-ui/core/IconButton';
import TableCell from '@material-ui/core/TableCell';
import TablePagination from '@material-ui/core/TablePagination';
import TableRow from '@material-ui/core/TableRow';

import DescriptionIcon from '@mui/icons-material/Description';
import { Button, Text, Select, Table, DatePicker } from 'components';
import { saveAs } from 'file-saver';
import { ErrorMessage, Field, Form, Formik } from 'formik';
import { PageTitle } from 'pages/Layout/PageTitle';
import { launchPrivider } from 'services';
import * as Yup from 'yup';

import { FormatDate } from '../../../../components/FormatDate';
import { DialogVincClients } from './Dialogs/DialogVincClients';

const validationSchema = Yup.object().shape({
  value: Yup.string().when('type_info', {
    is: (val) => val !== 'date',
    then: Yup.string().required('Campo obrigatório'),
  }),
  input_data: Yup.date().when('type_info', {
    is: 'date',
    then: Yup.date().required('Campo obrigatório'),
  }),
  output_data: Yup.date().when('type_info', {
    is: 'date',
    then: Yup.date()
      .required('Campo obrigatório')
      .min(Yup.ref('input_data'), 'Data final não pode ser anterior à data inicial')
      .test(
        'max-15-days',
        'O intervalo entre as datas não pode ser maior que 15 dias',
        function (outputDate) {
          const { input_data } = this.parent;
          if (!input_data || !outputDate) return true;
          const diffTime = Math.abs(new Date(outputDate) - new Date(input_data));
          const diffDays = diffTime / (1000 * 60 * 60 * 24);
          return diffDays <= 15;
        },
      ),
  }),
});

const type = [
  { value: 'ordem_serviço', label: 'Ordem de Serviço' },
  { value: 'serial', label: 'Serial' },
  { value: 'date', label: 'Data' },
];

const columns = [
  { label: 'OS CAR', align: 'center' },
  { label: 'Serial', align: 'center' },
  { label: 'Cliente', align: 'center' },
  { label: 'Data', align: 'center' },
  { label: 'Laudo Perda', align: 'center' },
];

export const SystemReport = () => {
  const [loading, setIsLoading] = useState(false);
  const [openDialogVincClient, setOpenDialogVincClient] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [rowData, setRowData] = useState([]);
  const jsonObject = JSON.parse(localStorage.getItem('@Gaia:user'));

  const hanldeGetDownloadLaudo = async (item) => {
    toast.info('Aguarde o Laudo ser gerado');
    setIsLoading(true);
    try {
      const { data } = await launchPrivider.systemReportGenerator({ numero_os: item?.numeroos });
      const file = new Blob([data], { type: 'application/pdf' });
      saveAs(file, `laudo-${item.numeroos || 'arquivo'}.pdf`);
      toast.success('Laudo gerado com sucesso!');
    } catch (error) {
      toast.error(error.response.data.message || 'Erro ao gerar PDF!');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (values) => {
    setIsLoading(true);
    try {
      const { data } = await launchPrivider.getLaudosByServiceOrder({
        type_info: values.type_info,
        value: values.value,
        input_data: values.input_data,
        output_data: values.output_data,
      });
      if (data?.length <= 0) {
        toast.error('A consulta não retornou resultado!');
        setIsLoading(false);
        return;
      }
      setRowData(data);
      toast.success('Consulta realizada com sucesso!');
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Erro ao realizar busca pelo laudo!');
    } finally {
      setIsLoading(false);
    }
  };

  const handleOpenDialogVincClients = () => {
    setOpenDialogVincClient(true);
  };

  const handleCloseDialogVincClients = () => {
    setOpenDialogVincClient(false);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const paginatedData = rowData?.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  return (
    <>
      <DialogVincClients open={openDialogVincClient} onClose={handleCloseDialogVincClients} />
      <PageTitle>Sistema de Laudo</PageTitle>
      <Formik
        onSubmit={handleSubmit}
        initialValues={{
          type_info: 'ordem_serviço',
          value: '',
          input_data: new Date(),
          output_data: new Date(),
        }}
        validationSchema={validationSchema}
      >
        {(props) => (
          <Form>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={12} md={2} lg={2}>
                <Field
                  size='small'
                  fullWidth
                  name='type_info'
                  variant='outlined'
                  label='Tipo'
                  options={type}
                  component={Select}
                  loading={loading}
                />
              </Grid>
              {props.values.type_info !== 'date' && (
                <Grid item xs={12} sm={12} md={2} lg={2}>
                  <Field
                    variant='outlined'
                    label=''
                    size='small'
                    fullWidth
                    name='value'
                    component={Text}
                    loading={loading}
                  />
                </Grid>
              )}
              {props.values.type_info === 'date' && (
                <>
                  <Grid item xs={12} md={3} lg={2}>
                    <Field
                      size='small'
                      name='input_data'
                      variant='inline'
                      fullWidth
                      inputVariant='outlined'
                      label='Data inicial'
                      component={DatePicker}
                      loading={loading}
                    />
                    <ErrorMessage
                      name='input_data'
                      component='div'
                      style={{ color: 'red', fontSize: '12px' }}
                    />
                  </Grid>
                  <Grid item xs={12} md={3} lg={2}>
                    <Field
                      size='small'
                      name='output_data'
                      variant='inline'
                      inputVariant='outlined'
                      label='Data final'
                      component={DatePicker}
                      loading={loading}
                    />
                    <ErrorMessage
                      name='output_data'
                      component='div'
                      style={{ color: 'red', fontSize: '12px' }}
                    />
                  </Grid>
                </>
              )}
              <Grid item xs={12} md={2}>
                <Button type='submit' fullWidth loading={loading}>
                  Consultar
                </Button>
              </Grid>
              {[5, 6].includes(jsonObject?.role.id) && (
                <Grid item xs={12} md={2}>
                  <Button fullWidth loading={loading} onClick={handleOpenDialogVincClients}>
                    Vinculo de Clientes
                  </Button>
                </Grid>
              )}
            </Grid>

            <Box mt={3}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Table
                    name='Table'
                    maxHeight
                    headers={columns}
                    emptyMessage='Nenhum registro encontrado.'
                    disableNumeration
                  >
                    {paginatedData?.map((item) => (
                      <TableRow key={item?.numeroOs}>
                        <TableCell align='center'>{item?.numeroos}</TableCell>
                        <TableCell align='center'>{item?.serial}</TableCell>
                        <TableCell align='center'>{item?.cliente}</TableCell>
                        <TableCell align='center'>
                          <FormatDate format='dd/MM/yyyy'>{item?.dtCriacaoLaudo}</FormatDate>
                        </TableCell>
                        <TableCell align='center'>
                          <IconButton
                            color='primary'
                            onClick={() => hanldeGetDownloadLaudo(item)}
                            disabled={loading}
                          >
                            <DescriptionIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </Table>

                  <TablePagination
                    component='div'
                    count={rowData?.length}
                    page={page}
                    onPageChange={handleChangePage}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    rowsPerPageOptions={[5, 10, 25, 50]}
                    labelRowsPerPage='Linhas por página'
                  />
                </Grid>
              </Grid>
            </Box>
          </Form>
        )}
      </Formik>
    </>
  );
};
